/**
 * Utility to parse and extract file references from citation preview text.
 * Handles file names in square brackets format: [filename.pdf]
 */

export interface ParsedTextPart {
  type: 'text' | 'file'
  content: string
  fileName?: string
}

/**
 * Regex pattern to match file names in square brackets
 * Matches: [filename.ext], [document.pdf], [my file.docx], etc.
 * File names can contain spaces, dots, and common special characters
 */
const FILE_BRACKET_PATTERN =
  /(?<!\[)\[([^[\]]+\.(?:pdf|doc|docx|xls|xlsx|csv|txt|ppt|pptx|png|jpg|jpeg|gif|webp))\](?!\])/gi

/**
 * Parse citation preview text and extract file references in square brackets
 * @param text - The preview text to parse
 * @returns Array of parsed parts with type ('text' or 'file') and content
 * @example
 * parseCitationText("Check [document.pdf] for details")
 * // Returns: [
 * //   { type: 'text', content: 'Check ' },
 * //   { type: 'file', content: '[document.pdf]', fileName: 'document.pdf' },
 * //   { type: 'text', content: ' for details' }
 * // ]
 */
export const parseCitationText = (text: string): ParsedTextPart[] => {
  if (!text) return []

  const parts: ParsedTextPart[] = []
  let lastIndex = 0
  let match

  // Reset lastIndex for global regex
  FILE_BRACKET_PATTERN.lastIndex = 0

  while ((match = FILE_BRACKET_PATTERN.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex, match.index)
      })
    }

    // Add the file reference
    const fileName = match[1] // Captured group without brackets
    parts.push({
      type: 'file',
      content: match[0], // Full match including brackets
      fileName
    })

    lastIndex = FILE_BRACKET_PATTERN.lastIndex
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex)
    })
  }

  return parts.length > 0 ? parts : [{ type: 'text', content: text }]
}
